import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Grid,
  MenuItem,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { productAPI } from '../../services/api';
import { setProducts } from '../../store/slices/productSlice';

// Default categories with MongoDB compatible IDs
const DEFAULT_CATEGORIES = [
  { _id: 'male', name: 'Male' },
  { _id: 'female', name: 'Female' },
  { _id: 'kids', name: 'Kids' }
];

const ProductsManagement = () => {
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.products);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    image: '',
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initData = async () => {
      await loadCategories();
      await loadProducts();
    };
    
    initData();
  }, []);

  const loadProducts = async () => {
    try {
      console.log('Loading products...');
      const response = await productAPI.getAll();
      console.log('Product API response:', response);
      
      // Handle different response formats to ensure we always have an array
      let productsData = [];
      if (response && response.data) {
        console.log('Response data type:', typeof response.data);
        console.log('Response data:', response.data);
        
        if (Array.isArray(response.data)) {
          console.log('Data is an array');
          productsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          console.log('Data is nested in data property');
          productsData = response.data.data;
        } else if (typeof response.data === 'object') {
          console.log('Data is an object, checking properties');
          // Try to find an array property in the response
          for (const key in response.data) {
            if (Array.isArray(response.data[key])) {
              console.log(`Found array in property: ${key}`);
              productsData = response.data[key];
              break;
            }
          }
        }
      }
      
      console.log('Products data before transformation:', productsData);
      
      // Transform products to ensure category is in the correct format
      const transformedProducts = productsData.map(product => {
        // If category is a string, convert it to an object
        if (typeof product.category === 'string') {
          console.log(`Product ${product.name} has string category: ${product.category}`);
          // Try to find in our categories list
          const categoryObj = categories.find(c => c._id === product.category || c.name === product.category);
          if (categoryObj) {
            return {
              ...product,
              category: categoryObj
            };
          }
          // If not found, create a new category object
          return {
            ...product,
            category: {
              _id: product.category,
              name: product.category.charAt(0).toUpperCase() + product.category.slice(1)
            }
          };
        }
        
        // If category is missing or null, assign to Uncategorized
        if (!product.category) {
          console.log(`Product ${product.name} has no category, assigning to Uncategorized`);
          return {
            ...product,
            category: {
              _id: 'uncategorized',
              name: 'Uncategorized'
            }
          };
        }
        
        // If category exists but doesn't have a name, add name property
        if (product.category && !product.category.name && product.category._id) {
          console.log(`Product ${product.name} has category ID but no name: ${product.category._id}`);
          // Try to find in our categories list
          const categoryObj = categories.find(c => c._id === product.category._id);
          if (categoryObj) {
            return {
              ...product,
              category: categoryObj
            };
          }
          return {
            ...product,
            category: {
              ...product.category,
              name: product.category._id.charAt(0).toUpperCase() + product.category._id.slice(1)
            }
          };
        }
        
        return product;
      });
      
      console.log('Transformed products:', transformedProducts);
      dispatch(setProducts(transformedProducts));
    } catch (error) {
      console.error('Failed to load products:', error);
      dispatch(setProducts([]));
      toast.error('Failed to load products');
    }
  };

  const loadCategories = async () => {
    try {
      console.log('Loading categories...');
      
      // Prepare our default categories as a base
      const combinedCategories = [...DEFAULT_CATEGORIES];
      
      // Add Uncategorized option if not already included
      if (!combinedCategories.some(c => c._id === 'uncategorized')) {
        combinedCategories.push({ _id: 'uncategorized', name: 'Uncategorized' });
      }
      
      // Get unique categories from existing products
      try {
        console.log('Getting product categories...');
        const response = await productAPI.getAll();
        let productsData = [];
        
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            productsData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            productsData = response.data.data;
          }
        }
        
        // Extract unique categories from products
        if (productsData.length > 0) {
          console.log('Extracting categories from products...');
          const productCategories = productsData
            .filter(p => p.category) // Filter out products without categories
            .map(p => {
              // Handle both string and object categories
              if (typeof p.category === 'string') {
                return {
                  _id: p.category,
                  name: p.category.charAt(0).toUpperCase() + p.category.slice(1)
                };
              } else if (p.category._id) {
                return {
                  _id: p.category._id,
                  name: p.category.name || (p.category._id.charAt(0).toUpperCase() + p.category._id.slice(1))
                };
              }
              return null;
            })
            .filter(Boolean); // Remove nulls
          
          // Create a Set of category IDs we already have
          const existingCategoryIds = new Set(combinedCategories.map(c => c._id));
          
          // Add unique categories from products
          productCategories.forEach(category => {
            if (!existingCategoryIds.has(category._id)) {
              combinedCategories.push(category);
              existingCategoryIds.add(category._id);
            }
          });
        }
      } catch (error) {
        console.error('Error getting categories from products:', error);
        // Continue with default categories
      }
      
      console.log('Final categories:', combinedCategories);
      setCategories(combinedCategories);
    } catch (error) {
      console.error('Failed to load categories:', error);
      
      // Silently use default categories without showing a warning
      const fallbackCategories = [...DEFAULT_CATEGORIES];
      if (!fallbackCategories.some(c => c._id === 'uncategorized')) {
        fallbackCategories.push({ _id: 'uncategorized', name: 'Uncategorized' });
      }
      setCategories(fallbackCategories);
      
      // Only show error toast if we can't even load default categories
      if (fallbackCategories.length === 0) {
        toast.error('Error loading categories');
      }
    }
  };

  const handleOpen = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        category: product.category?._id || 'uncategorized',
        stock: product.stock || '',
        image: product.image || '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category: '',
        stock: '',
        image: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      stock: '',
      image: '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Create a copy of the form data for submission
      const productData = {...formData};
      
      // For editing: If a field is empty and we're editing, don't update that field
      if (editingProduct) {
        // Create an object with only the fields that were changed
        const updatedFields = {};
        
        // Only include fields that have values and are different from original
        if (productData.name && productData.name !== editingProduct.name) 
          updatedFields.name = productData.name;
        
        if (productData.description && productData.description !== editingProduct.description) 
          updatedFields.description = productData.description;
        
        if (productData.price && productData.price.toString() !== editingProduct.price.toString()) 
          updatedFields.price = productData.price;
        
        if (productData.category && productData.category !== editingProduct.category?._id) 
          updatedFields.category = productData.category;
        
        if (productData.stock && productData.stock.toString() !== editingProduct.stock.toString()) 
          updatedFields.stock = productData.stock;
        
        if (productData.image && productData.image !== editingProduct.image) 
          updatedFields.image = productData.image;
        
        // If category is a default category, it needs to be treated differently
        const isDefaultCategory = DEFAULT_CATEGORIES.some(c => c._id === productData.category);
        if (isDefaultCategory) {
          // For default categories, we use the string ID directly
          // The backend will handle creating/retrieving the appropriate category
        }
        
        // Only send update if at least one field was changed
        if (Object.keys(updatedFields).length > 0) {
          await productAPI.update(editingProduct._id, updatedFields);
          toast.success('Product updated successfully');
        } else {
          toast.info('No changes were made to the product');
        }
      } else {
        // For new products, all fields are required
        if (!productData.name || !productData.description || !productData.price || 
            !productData.category || !productData.stock || !productData.image) {
          toast.error('All fields are required when creating a new product');
          setLoading(false);
          return;
        }
        
        await productAPI.create(productData);
        toast.success('Product created successfully');
      }
      
      handleClose();
      // Reload after a short delay to give the backend time to process
      setTimeout(loadProducts, 500);
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productAPI.delete(id);
        toast.success('Product deleted successfully');
        loadProducts();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  // Get category name from ID for display
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c._id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Products Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Product
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(products) && products.length > 0 ? (
              products.map((product) => (
                <TableRow key={product._id}>
                  <TableCell>
                    <img
                      src={product.image || "https://via.placeholder.com/50x50?text=No+Image"}
                      alt={product.name}
                      style={{ width: 50, height: 50, objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/50x50?text=Error";
                      }}
                    />
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>
                    {product.category?.name || getCategoryName(product.category) || "Uncategorized"}
                  </TableCell>
                  <TableCell>${product.price}</TableCell>
                  <TableCell>{product.stock}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpen(product)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(product._id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No products found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!editingProduct}
                  helperText={editingProduct ? "Leave unchanged to keep current value" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  required={!editingProduct}
                  helperText={editingProduct ? "Leave unchanged to keep current value" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  required={!editingProduct}
                  helperText={editingProduct ? "Leave unchanged to keep current value" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Stock"
                  name="stock"
                  type="number"
                  value={formData.stock}
                  onChange={handleChange}
                  required={!editingProduct}
                  helperText={editingProduct ? "Leave unchanged to keep current value" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required={!editingProduct}
                  helperText={editingProduct ? "Select a category or leave unchanged" : "Select a category for the product"}
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {/* Default Categories Group */}
                  <MenuItem disabled style={{ opacity: 0.6, fontWeight: 'bold' }}>
                    Default Categories
                  </MenuItem>
                  {DEFAULT_CATEGORIES.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                  
                  {/* Other Categories Group */}
                  {categories.length > DEFAULT_CATEGORIES.length && (
                    <MenuItem disabled style={{ opacity: 0.6, fontWeight: 'bold' }}>
                      Other Categories
                    </MenuItem>
                  )}
                  {categories
                    .filter(cat => !DEFAULT_CATEGORIES.some(dc => dc._id === cat._id) && cat._id !== 'uncategorized')
                    .map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Image URL"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  required={!editingProduct}
                  helperText={editingProduct ? "Leave unchanged to keep current value" : ""}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : editingProduct ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProductsManagement; 